'use client'

import { MAPTILER_API_TOKEN } from '@components/app/LocationMap/consts'
import { GeolocationContext } from '@components/hooks/GeolocationProvider/index.client'
import { classMerge } from '@components/utilities/classMerge'
import ExternalRoutes from '@lib/routes/ExternalRoutes'
import { type ClinicalTrialForListingPage } from '@modules/trials/helpers/fragments/clinicalTrialListingFragment'
import { hasAvailableCountry } from '@modules/trials/helpers/hasAvailableCountry'
import { findClosestLocation } from '@modules/trials/helpers/parseLocationsFromTrial/parseTruncatedLocationsFromTrial'
import type { TrialInDatabaseForAlgolia } from '@modules/trials/types/TrialInDatabaseForAlgolia'
import maplibregl from 'maplibre-gl' // Using MapLibre alt: http://visgl.github.io/react-map-gl/docs/get-started/get-started#using-with-a-mapbox-gl-fork
import 'maplibre-gl/dist/maplibre-gl.css'
import dynamic from 'next/dynamic'
import { useContext, useEffect, useState } from 'react'
import { LocationPin } from './LocationPin'
import { Trial2MapSidebar } from './Trial2MapSidebar'

// Use deep imports to only include used leaves in the ReactMap GL tree
const DynamicMap = dynamic(
  () => import('react-map-gl/dist/es5/components/map'),
  { loading: () => <div>Loading ...</div>, ssr: false },
)
const NavigationControl = dynamic(
  () => import('react-map-gl/dist/es5/components/navigation-control'),
  { loading: () => <div>Loading ...</div>, ssr: false },
)

export type TrialLocationInMap =
  | ClinicalTrialForListingPage['trialLocations'][0]
  | TrialInDatabaseForAlgolia['trialLocations'][0]

interface LocationMapProps {
  mapContainerClassName?: string
  showSiteLinks?: boolean
  showTrialLink?: boolean
  tallContainer?: boolean
  trialLocations: TrialLocationInMap[]
}

export default function LocationMap({
  mapContainerClassName,
  trialLocations,
}: LocationMapProps) {
  const userLocation = useContext(GeolocationContext)
  const closestSiteData = findClosestLocation(trialLocations, userLocation)

  const locationsToRender = trialLocations
    .filter((location) => hasAvailableCountry(location.country))
    .filter((location) => !!location.lat && !!location.lng)

  // Center on the closest trial location, otherwise NYC as the default
  const latToCenter = parseFloat(closestSiteData?.location?.lat ?? '40.71')
  const lngToCenter = parseFloat(closestSiteData?.location?.lng ?? '-74.00')
  const [mapPositionState, setMapPositionState] = useState({
    latitude: latToCenter,
    longitude: lngToCenter,
    zoom: locationsToRender.length === 1 ? 12 : 3,
  })

  const [selectedIndex, setSelectedIndex] = useState<number>(-1)
  const [hoveredIndex, setHoveredIndex] = useState<number>(-1)

  useEffect(() => {
    const selectedLocation = locationsToRender[selectedIndex]
    if (selectedLocation && selectedLocation.lat && selectedLocation.lng) {
      setMapPositionState({
        latitude: parseFloat(selectedLocation.lat),
        longitude: parseFloat(selectedLocation.lng),
        zoom: mapPositionState.zoom,
      })
    }
  }, [selectedIndex])

  return (
    <div className='flex w-full flex-wrap-reverse overflow-hidden rounded-2xl border border-neutral200'>
      <Trial2MapSidebar
        locationsToRender={locationsToRender}
        selectedIndex={selectedIndex}
        setHoveredIndex={setHoveredIndex}
        setSelectedIndex={setSelectedIndex}
      />
      <div
        className={classMerge(
          'w-full overflow-hidden md:w-1/2',
          'h-72 md:h-[500px]',
          mapContainerClassName,
        )}
      >
        <DynamicMap
          {...mapPositionState}
          attributionControl={false}
          mapLib={maplibregl}
          mapStyle={ExternalRoutes.mapTilerStyleUrl(MAPTILER_API_TOKEN)}
          onMove={(evt) => setMapPositionState(evt.viewState)}
          style={{ height: '100%', width: '100%' }}
        >
          <NavigationControl />
          {locationsToRender.map((location, index) => (
            <LocationPin
              key={location.id}
              location={location}
              onClick={() => {
                setSelectedIndex(index)
              }}
              selected={index === selectedIndex || index === hoveredIndex}
            />
          ))}
        </DynamicMap>
      </div>
    </div>
  )
}
